import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import InstallDesktopRoundedIcon from '@mui/icons-material/InstallDesktopRounded';
import { useAuth } from '../context/AuthContext';
import {
  getNotificationPermission,
  isIosSafari,
  isPushSupported,
  isPwaInstalled,
  pushRequiresPwaInstall,
  subscribeToPush,
} from '../utils/push';

/* ──────────────────────────────────────────────────────────────────────────
 * Storage keys (kept identical to the previous standalone banners so users
 * who already dismissed something don't get re-nagged after this refactor).
 * ────────────────────────────────────────────────────────────────────────── */
const INSTALL_DISMISS_KEY = 'fabtraque.installAppBannerDismissedAt';
const INSTALL_SESSION_KEY = 'fabtraque.installShownThisSession';
const PUSH_STATE_KEY = 'fabtraque.pushOptInBannerState';
const PUSH_SESSION_KEY = 'fabtraque.pushOptInShownThisSession';

const INSTALL_REDISPLAY_DAYS = 14;
const PUSH_REDISPLAY_DAYS = {
  dismiss: 7,
  denied: 30,
  error: 1,
  soft: 2,
};

function readLs(key) { try { return window.localStorage.getItem(key); } catch { return null; } }
function writeLs(key, value) { try { window.localStorage.setItem(key, value); } catch {} }
function readSs(key) { try { return window.sessionStorage.getItem(key); } catch { return null; } }
function writeSs(key, value) { try { window.sessionStorage.setItem(key, value); } catch {} }

function installRecentlyDismissed() {
  const raw = readLs(INSTALL_DISMISS_KEY);
  if (!raw) return false;
  const at = parseInt(raw, 10);
  if (!Number.isFinite(at)) return false;
  return (Date.now() - at) < INSTALL_REDISPLAY_DAYS * 24 * 60 * 60 * 1000;
}
function pushRecentlyDismissed() {
  try {
    const raw = readLs(PUSH_STATE_KEY);
    const state = raw ? JSON.parse(raw) : null;
    if (!state || !state.dismissedAt || !state.reason) return false;
    const days = PUSH_REDISPLAY_DAYS[state.reason] ?? 7;
    return (Date.now() - state.dismissedAt) < days * 24 * 60 * 60 * 1000;
  } catch { return false; }
}

/* ──────────────────────────────────────────────────────────────────────────
 * Onboarding orchestrator
 * Phases: 'evaluating' → 'install' → 'push' → 'done'
 * The phase machine reacts to:
 *  - mount + user state changes (initial decision)
 *  - beforeinstallprompt event (Chromium may fire asynchronously after mount)
 *  - install resolution (success / dismiss) → re-evaluate push
 *  - push resolution → done
 * ────────────────────────────────────────────────────────────────────────── */
const NotificationOnboarding = () => {
  const { user } = useAuth();
  const ios = isIosSafari();

  const [phase, setPhase] = useState('evaluating');
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(() => isPwaInstalled());
  const [activating, setActivating] = useState(false);
  const [pushSuccess, setPushSuccess] = useState(false);
  const [pushError, setPushError] = useState(null);

  // Latest values referenced from event listeners that don't re-run.
  const pushInteractedRef = useRef(false);
  const phaseRef = useRef(phase);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  /* Async push subscription check — true if this device is already subscribed. */
  const checkAlreadySubscribed = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const existing = reg ? await reg.pushManager.getSubscription() : null;
      return Boolean(existing);
    } catch {
      return false;
    }
  }, []);

  /* Should we show the INSTALL step at all? */
  const canShowInstall = useCallback(() => {
    if (installed) return false;
    if (installRecentlyDismissed()) return false;
    if (readSs(INSTALL_SESSION_KEY) === '1') return false;
    // iOS Safari → we have step-by-step instructions, no programmatic prompt.
    // Other platforms → we need the beforeinstallprompt event (Chromium).
    return ios || installEvent != null;
  }, [installed, ios, installEvent]);

  /* Should we show the PUSH step at all? (sync portion; async subscription
     check is layered on top in the evaluator below.) */
  const canShowPushSync = useCallback(() => {
    if (!user) return false;
    if (!isPushSupported()) return false;
    if (pushRequiresPwaInstall() && !installed) return false; // iOS gate
    if (getNotificationPermission() === 'denied') return false;
    if (pushRecentlyDismissed()) return false;
    if (readSs(PUSH_SESSION_KEY) === '1') return false;
    return true;
  }, [user, installed]);

  /* Capture install events globally (one shot per session per platform). */
  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallEvent(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  /* Decide initial phase shortly after mount (delay lets beforeinstallprompt
     fire on Chromium, and avoids flashing UI during first paint / login). */
  useEffect(() => {
    if (!user) { setPhase('done'); return undefined; }
    let cancelled = false;
    const decide = async () => {
      // Install takes priority — the user installs first, then we offer push.
      if (canShowInstall()) {
        if (!cancelled) {
          writeSs(INSTALL_SESSION_KEY, '1');
          setPhase('install');
        }
        return;
      }
      // Otherwise check push.
      if (!canShowPushSync()) { if (!cancelled) setPhase('done'); return; }
      const subscribed = await checkAlreadySubscribed();
      if (cancelled) return;
      if (subscribed) { setPhase('done'); return; }
      writeSs(PUSH_SESSION_KEY, '1');
      pushInteractedRef.current = false;
      setPhase('push');
    };
    // 2.5 s delay so we don't fight first-paint and post-login modals
    // (password-expiry dialog, etc.).
    const t = setTimeout(decide, 2500);
    return () => { cancelled = true; clearTimeout(t); };
    // canShowInstall / canShowPushSync read fresh state — re-run when their
    // dependencies change (user, installed, installEvent).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, installed, installEvent]);

  /* Re-evaluate push after the install step closes. Runs whenever we leave
     the 'install' phase via dismiss/install (phase moves to a transient 'done'
     intermediate and then maybe to 'push'). */
  const advanceFromInstall = useCallback(async () => {
    if (!canShowPushSync()) { setPhase('done'); return; }
    const subscribed = await checkAlreadySubscribed();
    if (subscribed) { setPhase('done'); return; }
    writeSs(PUSH_SESSION_KEY, '1');
    pushInteractedRef.current = false;
    // Tiny delay so the install banner has visibly closed before the modal
    // opens — feels like two discrete steps, not a jump.
    setTimeout(() => setPhase('push'), 600);
  }, [canShowPushSync, checkAlreadySubscribed]);

  /* Soft-dismiss on tab close while push modal is open without interaction. */
  useEffect(() => {
    if (phase !== 'push') return undefined;
    const onPageHide = () => {
      if (!pushInteractedRef.current) {
        try {
          writeLs(PUSH_STATE_KEY, JSON.stringify({ dismissedAt: Date.now(), reason: 'soft' }));
        } catch {}
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [phase]);

  /* ── Install handlers ──────────────────────────────────────────────────── */
  const handleInstallDismiss = useCallback(() => {
    writeLs(INSTALL_DISMISS_KEY, String(Date.now()));
    setPhase('evaluating'); // re-decide → likely 'push' next
    setTimeout(() => advanceFromInstall(), 0);
  }, [advanceFromInstall]);

  const handleInstallClick = useCallback(async () => {
    if (!installEvent) return;
    try {
      installEvent.prompt();
      const choice = await installEvent.userChoice;
      setInstallEvent(null);
      if (choice.outcome === 'accepted') {
        setInstalled(true);
        // appinstalled event will fire too; either way move forward.
        await advanceFromInstall();
      } else {
        // User refused the OS install prompt — back off + move on.
        writeLs(INSTALL_DISMISS_KEY, String(Date.now()));
        await advanceFromInstall();
      }
    } catch {
      // prompt() can only fire once — fall through and continue with push.
      await advanceFromInstall();
    }
  }, [installEvent, advanceFromInstall]);

  /* ── Push handlers ─────────────────────────────────────────────────────── */
  const dismissPush = useCallback((reason) => {
    pushInteractedRef.current = true;
    try {
      writeLs(PUSH_STATE_KEY, JSON.stringify({ dismissedAt: Date.now(), reason }));
    } catch {}
    setPhase('done');
    setPushError(null);
    setPushSuccess(false);
  }, []);

  const handlePushActivate = useCallback(async () => {
    pushInteractedRef.current = true;
    setActivating(true);
    setPushError(null);
    try {
      await subscribeToPush();
      setPushSuccess(true);
      setTimeout(() => setPhase('done'), 1800);
    } catch (err) {
      const msg = err?.message || 'Activation échouée.';
      setPushError(msg);
      if (msg.toLowerCase().includes('refusée') || msg.toLowerCase().includes('denied')) {
        try {
          writeLs(PUSH_STATE_KEY, JSON.stringify({ dismissedAt: Date.now(), reason: 'denied' }));
        } catch {}
      }
    } finally {
      setActivating(false);
    }
  }, []);

  /* ── Render ────────────────────────────────────────────────────────────── */

  if (phase === 'install') {
    return (
      <Dialog
        open
        onClose={handleInstallDismiss}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pr: 6, pb: 0.5 }}>
          {ios ? 'Installer sur l\'écran d\'accueil' : 'Installer FabTraque ?'}
          <IconButton
            aria-label="Fermer"
            onClick={handleInstallDismiss}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 2.5 }}>
            <Box
              sx={{
                width: 80, height: 80, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: 'primary.lighter',
                border: '4px solid',
                borderColor: 'primary.light',
              }}
            >
              {ios
                ? <IosShareRoundedIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                : <InstallDesktopRoundedIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
            </Box>
          </Box>

          {ios ? (
            <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.primary' }}>
              Touchez <strong>Partager</strong> en bas de Safari, puis
              <strong> « Sur l'écran d'accueil »</strong>. Vous pourrez activer
              les notifications juste après.
            </Typography>
          ) : (
            <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.primary' }}>
              Accès plus rapide depuis votre bureau, dans sa propre fenêtre. Vous
              pourrez activer les notifications juste après.
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleInstallDismiss} color="inherit">
            {ios ? 'Plus tard' : 'Plus tard'}
          </Button>
          {!ios && installEvent && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleInstallClick}
              startIcon={<InstallDesktopRoundedIcon />}
              sx={{ minWidth: 160 }}
            >
              Installer
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }

  if (phase === 'push') {
    return (
      <Dialog
        open
        onClose={() => !activating && dismissPush('dismiss')}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pr: 6, pb: 0.5 }}>
          {pushSuccess ? 'Notifications activées' : 'Activer les notifications ?'}
          <IconButton
            aria-label="Fermer"
            onClick={() => !activating && dismissPush('dismiss')}
            disabled={activating}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 2.5 }}>
            <Box
              sx={{
                width: 80, height: 80, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: pushSuccess ? 'success.lighter' : 'primary.lighter',
                border: '4px solid',
                borderColor: pushSuccess ? 'success.light' : 'primary.light',
                transition: 'all 0.3s ease',
              }}
            >
              {pushSuccess
                ? <CheckCircleRoundedIcon sx={{ fontSize: 44, color: 'success.main' }} />
                : <NotificationsActiveOutlinedIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
            </Box>
          </Box>

          {pushSuccess ? (
            <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.primary' }}>
              Notifications activées sur cet appareil.
            </Typography>
          ) : (
            <>
              <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.primary' }}>
                Restez informé des événements importants, même quand l'app est fermée.
              </Typography>
              {pushError && (
                <Alert severity="error" sx={{ mt: 2 }}>{pushError}</Alert>
              )}
            </>
          )}
        </DialogContent>

        {!pushSuccess && (
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button
              onClick={() => dismissPush('dismiss')}
              disabled={activating}
              color="inherit"
            >
              Plus tard
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePushActivate}
              disabled={activating}
              startIcon={activating
                ? <CircularProgress size={16} sx={{ color: 'inherit' }} thickness={5} />
                : <NotificationsActiveOutlinedIcon />}
              sx={{ minWidth: 160 }}
            >
              {activating ? 'Activation…' : 'Activer maintenant'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    );
  }

  return null;
};

export default NotificationOnboarding;
